import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  User, Database, FlaskConical, Loader2, Trash2, Sparkles, Info,
} from "lucide-react";

export default function SettingsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [seeding, setSeeding] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const seed = async () => {
    setSeeding(true);
    try {
      const res = await api.post("/testdata/seed");
      toast.success(res.data.message);
      qc.invalidateQueries();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Fehler");
    } finally {
      setSeeding(false);
    }
  };

  const deleteDemo = async () => {
    setDeleting(true);
    try {
      const res = await api.delete("/testdata");
      toast.success(res.data.message);
      qc.invalidateQueries();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Fehler");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 fade-in max-w-3xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Einstellungen</h1>
        <p className="text-sm text-muted-foreground mt-1">Konto und Testdaten verwalten.</p>
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <h2 className="text-lg font-semibold flex items-center gap-2"><User className="h-5 w-5 text-blue-400" />Konto</h2>
        <div className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between border-b border-border pb-2"><span className="text-muted-foreground">Name</span><span className="font-medium">{user?.name || "—"}</span></div>
          <div className="flex justify-between border-b border-border pb-2"><span className="text-muted-foreground">E-Mail</span><span className="font-medium">{user?.email}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Rolle</span><span className="font-medium capitalize">{user?.role}</span></div>
        </div>
        <p className="mt-3 text-xs text-muted-foreground flex items-start gap-1.5">
          <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          Zugangsdaten werden serverseitig über Umgebungsvariablen (ADMIN_EMAIL / ADMIN_PASSWORD) verwaltet.
        </p>
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <h2 className="text-lg font-semibold flex items-center gap-2"><FlaskConical className="h-5 w-5 text-amber-400" />Testdaten</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Lege einige klar mit „[DEMO]“ gekennzeichnete Test-Leads an, um das CRM auszuprobieren. Diese lassen sich jederzeit vollständig entfernen.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button onClick={seed} disabled={seeding} data-testid="seed-demo-btn">
            {seeding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Demo-Leads anlegen
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" className="text-rose-400 hover:text-rose-300" disabled={deleting} data-testid="delete-demo-btn">
                {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Alle Demo-Leads löschen
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Alle Demo-Leads löschen?</AlertDialogTitle>
                <AlertDialogDescription>Es werden ausschließlich die als [DEMO] markierten Test-Leads samt Verlauf entfernt. Echte Leads bleiben erhalten.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction onClick={deleteDemo} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-demo">Löschen</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <h2 className="text-lg font-semibold flex items-center gap-2"><Database className="h-5 w-5 text-emerald-400" />Daten &amp; Backup</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Alle CRM-Daten liegen persistent in MongoDB (Collections: <span className="font-mono text-xs text-sky-400">leads, activities, followups, users</span>).
          Ein Export bzw. Backup ist über einen MongoDB-Dump (<span className="font-mono text-xs text-sky-400">mongodump</span>) jederzeit möglich.
        </p>
      </div>
    </div>
  );
}
